// URL deep-link contract shared between GlobalSearch (writer) and the target
// pages (readers). Keep param names in one place so both sides stay in sync.

import { SearchItem } from "./types";
import { DateMatch } from "./dateParser";

export const DEEP_LINK_PARAMS = {
  groupBy: "gb", // "feeder" | "plant" | "substation" | "discom_substation" | "circle"
  entityId: "eid", // entity id (numeric for all entities incl. circle_id)
  entityLabel: "elabel", // display name
  plantType: "ptype", // plant_type, only for plant
  weatherParam: "wparam", // WEATHER_PARAMS key, enables Weather variable on Chart
  forecast: "fc", // "1" → enable the Forecast variable on Chart
  date: "dt", // YYYY-MM-DD single day
  dateStart: "ds", // YYYY-MM-DD range start
  dateEnd: "de", // YYYY-MM-DD range end
} as const;

/** Query tokens that mean "show forecast". */
export const FORECAST_TOKENS = new Set([
  "forecast",
  "forecasts",
  "forecasted",
  "forecasting",
  "forcast", // common misspelling
  "prediction",
  "predicted",
  "predict",
  "projected",
  "projection",
  "expected",
]);

/**
 * Map a search-query weather token → FeederDemand WEATHER_PARAMS key. Tokens are
 * the words a user is likely to type; kept here so search detection and the
 * Chart page's param list stay in sync (Chart owns the canonical key list).
 */
export const WEATHER_TOKEN_TO_PARAM: Record<string, string> = {
  // Temperature
  temperature: "temperature_2m",
  temp: "temperature_2m",
  temprature: "temperature_2m", // common misspelling
  tempreture: "temperature_2m",
  heat: "temperature_2m",
  thermal: "temperature_2m",
  // Humidity
  humidity: "relative_humidity_2m",
  humid: "relative_humidity_2m",
  moisture: "relative_humidity_2m",
  rh: "relative_humidity_2m",
  // Precipitation
  precipitation: "precipitation",
  precip: "precipitation",
  // Rain
  rain: "rain",
  rainfall: "rain",
  rainy: "rain",
  // Cloud
  cloud: "cloud_cover",
  clouds: "cloud_cover",
  cloudy: "cloud_cover",
  cloudcover: "cloud_cover",
  overcast: "cloud_cover",
  // Wind
  wind: "wind_speed_10m",
  windy: "wind_speed_10m",
  breeze: "wind_speed_10m",
  gust: "wind_speed_10m",
  // Sunshine
  sunshine: "sunshine_duration",
  sun: "sunshine_duration",
  sunny: "sunshine_duration",
  sunlight: "sunshine_duration",
  // Radiation
  radiation: "direct_radiation_instant",
  irradiance: "direct_radiation_instant",
  solar: "direct_radiation_instant",
  ghi: "direct_radiation_instant",
};

/**
 * Canonical weather words offered in suggestions / autocomplete (one display
 * word per param). Order = the order pills appear. Each maps to a param via
 * WEATHER_TOKEN_TO_PARAM.
 */
export const WEATHER_SUGGEST_WORDS = [
  "temperature",
  "humidity",
  "precipitation",
  "rain",
  "cloud",
  "wind",
  "sunshine",
  "radiation",
] as const;

/** Human label for a WEATHER_PARAMS key (for the row badge). */
export const WEATHER_PARAM_LABEL: Record<string, string> = {
  temperature_2m: "Temperature",
  relative_humidity_2m: "Humidity",
  precipitation: "Precipitation",
  rain: "Rain",
  cloud_cover: "Cloud Cover",
  wind_speed_10m: "Wind Speed",
  sunshine_duration: "Sunshine",
  direct_radiation_instant: "Direct Radiation",
};

/** Append date params to an existing URLSearchParams, when a date is present. */
function appendDateParams(p: URLSearchParams, date?: DateMatch): void {
  if (!date) return;
  if (date.date) {
    p.set(DEEP_LINK_PARAMS.date, date.date);
  } else if (date.startDate && date.endDate) {
    p.set(DEEP_LINK_PARAMS.dateStart, date.startDate);
    p.set(DEEP_LINK_PARAMS.dateEnd, date.endDate);
  }
}

/** Build the navigation target ("/jvvnl/...?gb=feeder&eid=...") for an item. */
export function buildNavTarget(
  item: SearchItem,
  targetKind?: "Chart" | "Metric",
  date?: DateMatch,
): string {
  if (item.hasDualViews) {
    const kind = targetKind || "Chart";
    if (kind === "Chart" && item.chartTarget) {
      const { page, deepLink } = item.chartTarget;
      const p = new URLSearchParams();
      p.set(DEEP_LINK_PARAMS.groupBy, deepLink.groupBy);
      p.set(DEEP_LINK_PARAMS.entityId, String(deepLink.value));
      p.set(DEEP_LINK_PARAMS.entityLabel, deepLink.label);
      if (deepLink.plantType) {
        p.set(DEEP_LINK_PARAMS.plantType, deepLink.plantType);
      }
      if (item.weatherParam) {
        p.set(DEEP_LINK_PARAMS.weatherParam, item.weatherParam);
      }
      if (item.forecast) {
        p.set(DEEP_LINK_PARAMS.forecast, "1");
      }
      appendDateParams(p, date);
      return `/${page}?${p.toString()}`;
    } else if (kind === "Metric" && item.metricTarget) {
      const { page, directQuery } = item.metricTarget;
      // Forecast works on the Metric page's forecast tab — append it.
      let q = item.forecast ? `${directQuery}&tab=forecast` : directQuery;
      const dp = new URLSearchParams();
      appendDateParams(dp, date);
      const dq = dp.toString();
      if (dq) q = `${q}&${dq}`;
      return `/${page}?${q}`;
    }
  }

  const base = `/${item.page}`;
  // Targets that read their own URL params (e.g. Metric page) win. A discom/
  // circle Metric-only result with the forecast flag gets the forecast tab too.
  if (item.directQuery) {
    let q =
      item.forecast && !/\btab=/.test(item.directQuery)
        ? `${item.directQuery}&tab=forecast`
        : item.directQuery;
    const dp = new URLSearchParams();
    appendDateParams(dp, date);
    const dq = dp.toString();
    if (dq) q = `${q}&${dq}`;
    return `${base}?${q}`;
  }
  // No deep-link, but a weather param / forecast flag to carry (e.g. a bare
  // weather or forecast metric): forward just those so the Chart page reacts.
  if (!item.deepLink) {
    const extra = new URLSearchParams();
    if (item.weatherParam)
      extra.set(DEEP_LINK_PARAMS.weatherParam, item.weatherParam);
    if (item.forecast) extra.set(DEEP_LINK_PARAMS.forecast, "1");
    appendDateParams(extra, date);
    const qs = extra.toString();
    return qs ? `${base}?${qs}` : base;
  }

  const p = new URLSearchParams();
  p.set(DEEP_LINK_PARAMS.groupBy, item.deepLink.groupBy);
  p.set(DEEP_LINK_PARAMS.entityId, String(item.deepLink.value));
  p.set(DEEP_LINK_PARAMS.entityLabel, item.deepLink.label);
  if (item.deepLink.plantType) {
    p.set(DEEP_LINK_PARAMS.plantType, item.deepLink.plantType);
  }
  if (item.weatherParam) {
    p.set(DEEP_LINK_PARAMS.weatherParam, item.weatherParam);
  }
  if (item.forecast) {
    p.set(DEEP_LINK_PARAMS.forecast, "1");
  }
  appendDateParams(p, date);
  return `${base}?${p.toString()}`;
}

export interface ParsedDeepLink {
  groupBy: "feeder" | "plant" | "substation" | "discom_substation" | "circle";
  /** Numeric for feeder/plant/substation/discom; string for circle. */
  entityId: number | string;
  entityLabel: string;
  plantType?: string;
  /** WEATHER_PARAMS key to enable on the Chart page, when present. */
  weatherParam?: string;
  /** True when the Chart page should enable the Forecast variable. */
  forecast?: boolean;
}

const VALID_GROUP_BY = new Set([
  "feeder",
  "plant",
  "substation",
  "discom_substation",
  "circle",
]);

/** Parse a deep-link out of a search string. Returns null when absent/invalid. */
export function parseDeepLink(search: string): ParsedDeepLink | null {
  const params = new URLSearchParams(search);
  const gb = params.get(DEEP_LINK_PARAMS.groupBy);
  const eid = params.get(DEEP_LINK_PARAMS.entityId);
  const elabel = params.get(DEEP_LINK_PARAMS.entityLabel);
  if (!gb || !VALID_GROUP_BY.has(gb) || !eid || !elabel) {
    return null;
  }
  // All entities (incl. circle, now a real circle_id) use positive numeric ids.
  // Kept permissive for circle for backward-compat with any stale "KOTPUTLI" url.
  let entityId: number | string = eid;
  const n = Number(eid);
  if (Number.isFinite(n) && n > 0) {
    entityId = n;
  } else if (gb !== "circle") {
    return null;
  }
  return {
    groupBy: gb as ParsedDeepLink["groupBy"],
    entityId,
    entityLabel: elabel,
    plantType: params.get(DEEP_LINK_PARAMS.plantType) ?? undefined,
    weatherParam: params.get(DEEP_LINK_PARAMS.weatherParam) ?? undefined,
    forecast: params.get(DEEP_LINK_PARAMS.forecast) === "1",
  };
}

/** Read date params (dt, or ds+de) out of a search string. Null when absent. */
export function parseDateParams(search: string): DateMatch | null {
  const params = new URLSearchParams(search);
  const dt = params.get(DEEP_LINK_PARAMS.date);
  if (dt) return { date: dt, label: dt, matchedTokens: [] };
  const ds = params.get(DEEP_LINK_PARAMS.dateStart);
  const de = params.get(DEEP_LINK_PARAMS.dateEnd);
  if (ds && de) return { startDate: ds, endDate: de, label: `${ds} - ${de}`, matchedTokens: [] };
  return null;
}
